import { LoadingChip } from '@app/components/base/LoadingChip';
import { webApi } from '@mi-front/api-clients';
import { Chip } from '@mui/material';
import { useIntl } from 'react-intl';
import { SocialExportUploadFailedStatusChip } from './SocialExportUploadFailedStatusChip';
import { socialExportPublishMessages } from './social-export-publish-messages';
import { SocialExportUploadCardDto } from './useSocialExportUploadCardActions';

interface SocialExportUploadStatusChipProps {
  /** Full BFF upload row; status and failed `data` drive the chip label. */
  upload: SocialExportUploadCardDto;
}

/**
 * Small Chip for social upload status. Reads status (and failed error details) from the upload DTO.
 */
export function SocialExportUploadStatusChip({ upload }: SocialExportUploadStatusChipProps) {
  const { $t } = useIntl();
  const { status } = upload;

  if (!status) {
    return null;
  }

  if (status === 'COMPLETED') {
    return (
      <Chip size="small" color="success" label={$t(socialExportPublishMessages.chipPublished)} />
    );
  }

  if (status === 'SCHEDULED') {
    return <Chip size="small" color="info" label={$t(socialExportPublishMessages.chipScheduled)} />;
  }

  if (status === 'WAITING' || status === 'WAITING_CHILDREN' || status === 'IN_PROGRESS') {
    return <LoadingChip label={$t(socialExportPublishMessages.chipInProgress)} />;
  }

  if (status === 'FAILED') {
    const failedData =
      upload.data?.status === 'FAILED' ? (upload.data as webApi.UploadFailedDataDto) : undefined;

    return (
      <SocialExportUploadFailedStatusChip
        error={failedData?.error}
        errorCode={failedData?.errorCode}
      />
    );
  }

  if (status === 'CANCELLED') {
    return (
      <Chip size="small" color="default" label={$t(socialExportPublishMessages.chipCancelled)} />
    );
  }

  return null;
}
